<template>
  <base-form
    ref="formRef"
    class="space-y-6"
    data-test-id="form-filter-price"
    :form
    validate-on="submit"
    @submit="handleSubmit"
  >
    <div class="grid cols-2 gap-4" data-test-id="vf-form-field-max">
      <vf-input :label="$t.min" :model-value="form.min">
        <span class="flex gap-1" :class="{ 'flex-row-reverse': !isCurrencyOnLeft }">
          <span v-if="form.min">
            {{ currencySymbol }}
          </span>
          <base-input
            v-model="form.min"
            :class="{ 'text-right': !isCurrencyOnLeft && form.min }"
            type="number"
            @focus="handleFocus"
            @input="handleInput"
            @keydown="handleKeyDown"
          />
        </span>
      </vf-input>
      <vf-form-field v-slot="{ attrs }" name="max" :rule="priceValidation">
        <vf-input v-bind="attrs" :label="$t.max" :model-value="form.max">
          <span class="flex gap-1" :class="{ 'flex-row-reverse': !isCurrencyOnLeft }">
            <span v-if="form.max">
              {{ currencySymbol }}
            </span>
            <base-input
              v-model="form.max"
              :aria-describedby="attrs['aria-describedby']"
              :class="{ 'text-right': !isCurrencyOnLeft && form.max }"
              type="number"
              @focus="handleFocus"
              @input="handleInput"
              @keydown="handleKeyDown"
            />
          </span>
        </vf-input>
      </vf-form-field>
    </div>
    <div class="flex items-center gap-4 wrap">
      <template v-for="button in buttonsOrder" :key="button">
        <vf-button v-if="button === 'apply'" :size="applyButtonSize" type="submit" :variant="applyButtonVariant">
          {{ $t.applyPrice }}
        </vf-button>
        <base-button v-if="button === 'reset'" class="text-sm underlined" :class="brandClasses?.reset" @click="handleReset">
          {{ $t.reset }}
        </base-button>
      </template>
    </div>
  </base-form>
</template>

<script lang="ts" setup>
import type { PriceFilter } from '#types/filters'

const props = defineProps<{
  filter: PriceFilter
  loading?: boolean
  currency: string
}>()

const {
  applyButtonVariant,
  applyButtonSize,
  buttonsOrder,
  brandClasses
} = useAppConfig().components.filter.option.priceInput

const { $formatPrice, $t, $viewport } = useNuxtApp()

const currencySymbol = getCurrencySymbol(useLocale(), props.currency)
const isCurrencyOnLeft = $formatPrice(0, props.currency).toString().startsWith(currencySymbol)
const model = defineModel<string[]>({ default: [] })

const formRef = useTemplateRef('formRef')
const form = reactive({
  min: model.value[0] ?? '',
  max: model.value[1] ?? ''
})

/**
 * Validates that `max` is required. `min` is not required and will default to 0.
 * If value of `max` is less than `min`, throw an error message.
 */
const priceValidation: Validator[] = [
  (max) => !max && ($t.pleaseEnterAValueAbove),
  (max, data) => {
    const _min = Number.parseFloat(data?.min || 0)
    const _max = Number.parseFloat(max || 0)
    const formattedMin = $formatPrice(_min, props.currency, { maximumFractionDigits: 0 })
    return _max <= _min && replaceAll($t.pleaseEnterAValueAboveMin, { _min: formattedMin })
  }
]

/**
 * Scrolls the price filter input into view after the virtual keyboard expandeds on mobile devices.
 */
const handleFocus = async (event: { target: HTMLInputElement }) => {
  if (!$viewport.lg) {
    setTimeout(() => {
      const closest = event.target?.closest<HTMLDivElement>('[data-scroll-el="filtersPanel"]')
      if (!closest) return
      const panelScroller = closest.parentElement
      const pillsSection = panelScroller?.querySelector<HTMLDivElement>('section.sticky')
      const filterAccordion = event.target?.closest<HTMLDivElement>('[data-scroll-el="filtersPanelAccordion"]')
      if (filterAccordion && pillsSection) {
        const nodeScrollY = filterAccordion.offsetTop - pillsSection.offsetHeight
        panelScroller?.scrollTo({ top: nodeScrollY })
      }
    }, 200)
  }
}

/**
 * Prevents non-number characters from being input on Android devices.
 */
const handleInput = (event: { data: string, target: HTMLInputElement }) => {
  if (/\D/.test(`${event.data}`)) {
    event.target.value = event.target.value.replace(/\D/g, '')
    form.min = `${form.min}`.replace(/\D/g, '')
    form.max = `${form.max}`.replace(/\D/g, '')
  }
}

/**
 * Price filter inputs only allow numbers. Using KeyboardEvent.key since KeyboardEvent.keyCode and KeyboardEvent.which are deprecated.
 */
const handleKeyDown = (event: KeyboardEvent) => {
  if (/\D/.test(event.key) && ![
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'Backspace',
    'Delete',
    'Enter',
    'Escape',
    'NumLock',
    'Tab'
  ].includes(event.key)) {
    event.preventDefault()
  }
}

const handleReset = () => {
  form.min = ''
  form.max = ''
  formRef.value?.reset()
  model.value = []
}

const handleSubmit = () => {
  form.min ||= '0'
  model.value = [form.min, form.max]
}

whenever(() => form.max, () => form.min ||= '0')
</script>
